/**
 * modalFormStyles — shared prototype-parity class strings for the dialog
 * forms (Suggest a resource / Suggest a change / Add a note).
 *
 * Single source of truth so the three dialog forms render the prototype's
 * modal field + button treatment identically and can't drift apart again
 * (the drift that left them in the old format after the #192 / #212 passes).
 *
 * Value parity is exact to design_handoff_peace_os/app (Peace OS.html
 * `<style>` + modals.jsx), mapped to the repo's @theme tokens:
 *   prototype --forest  #1B4332 -> sage-800       --sage    #52796F -> sage-400
 *   prototype --gold/--accent #C4A265 -> warm-400  --ink-soft #5B6B64 -> ink-soft
 *   prototype --sage-20 (rgba 82,121,111,.20) -> sage-400/20
 *   prototype --forest-08 (rgba 27,67,50,.08) -> sage-800/[8%]
 *   prototype --forest-06 (rgba 27,67,50,.06) -> sage-800/[6%]
 *   prototype --cream-2 #F2EDE3 -> sand (#F0EBE3, the repo's warm-neutral surface)
 *   prototype #E4EFE9 -> tick-affirm
 *   prototype --r-pill 999px -> rounded-full;  --r 12px -> rounded-xl
 */

import { type ReactNode } from 'react';
import { X } from 'lucide-react';

// .fld — 46px tall, 12px radius, sage-20 border, paper bg, 14.5px ink text;
// focus: sage border + 3px forest-06 ring; placeholder sage-300.
export const FLD =
  'w-full h-[46px] px-[14px] rounded-xl border border-sage-400/20 bg-paper text-[14.5px] text-ink placeholder:text-sage-300 outline-none transition-colors duration-[var(--dur-base)] focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';

// .fld for a multiline textarea — same treatment, auto height + resize-y.
export const FLD_TEXTAREA =
  'w-full px-[14px] py-[11px] rounded-xl border border-sage-400/20 bg-paper text-[14.5px] text-ink placeholder:text-sage-300 outline-none resize-y transition-colors duration-[var(--dur-base)] focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';

// The same box for a TextArea that grows with its text (`grow`): no
// resize handle, since the box sizes itself.
export const FLD_TEXTAREA_GROW = FLD_TEXTAREA.replace(' resize-y', '');

// A money box: the dollar sign drawn inside the box, and the input's
// left padding clearing it. The sign is aria-hidden; the label says
// "Hourly rate" and the help says "Dollars and cents".
export const FLD_MONEY = `${FLD} pl-[1.75rem]`;
export const FLD_MONEY_SIGN =
  'pointer-events-none absolute left-[0.875rem] top-1/2 -translate-y-1/2 text-[0.90625rem] leading-none text-ink-soft';

// A date box is the DateInput primitive (size "field" draws this box).

// .fld-label — 12.5px semibold sage, 7px below, .01em tracking.
export const FLD_LABEL =
  'block text-[12.5px] font-semibold text-sage-400 mb-[7px] tracking-[0.01em]';

// .fld-req — the required mark, in the label's own colour.
export const FLD_REQ = 'text-inherit';

// .fld-help — 12px ink-soft helper under a field.
export const FLD_HELP = 'text-xs text-ink-soft mt-1.5 leading-[1.45]';

// .btn — 42px pill, 14px semibold, 8px gap.
const BTN_BASE =
  'inline-flex items-center justify-center gap-2 h-[42px] px-5 rounded-full text-sm font-semibold transition-all duration-[var(--dur-base)] disabled:opacity-50 disabled:pointer-events-none';

// .btn.btn-ghost flattened (the prototype Cancel passes boxShadow:none + border:0).
export const BTN_GHOST = `${BTN_BASE} bg-paper text-sage-800 hover:text-forest-700`;

// .btn.btn-gold — accent bg, forest text (the suggest flows' "Send for review").
export const BTN_GOLD = `${BTN_BASE} bg-warm-400 text-sage-800 hover:brightness-[0.96]`;

// .btn.btn-forest — forest bg, cream text (a direct commit, e.g. "Share note").
export const BTN_FOREST = `${BTN_BASE} bg-sage-800 text-cream shadow-sh1 hover:bg-forest-700 hover:shadow-sh2`;

// .modal-foot — bottom action bar: border-top, cream-2 (sand) bg, right-aligned.
export const MODAL_FOOT =
  'flex items-center justify-end gap-4 px-[26px] py-4 border-t border-sage-800/[8%] bg-sand';

// .checkrow / .cbox — the prototype's check-list row + box.
export const CHECKROW =
  'flex items-center gap-[11px] py-[9px] px-2 rounded-[9px] cursor-pointer hover:bg-sand transition-colors';
export const CBOX =
  'w-[19px] h-[19px] shrink-0 rounded-[5px] border-[1.5px] flex items-center justify-center text-cream transition-all';
export const CBOX_OFF = 'border-sage-300';
export const CBOX_ON = 'bg-sage-800 border-sage-800';

// .chip-add / .chip-on — additive + selected coverage chips.
export const CHIP_ADD =
  'inline-flex items-center gap-[5px] h-8 px-[13px] rounded-full border border-dashed border-sage-300 bg-transparent text-sage-400 text-[13px] font-semibold cursor-pointer hover:border-sage-400 hover:text-sage-800 hover:bg-sage-800/[6%] transition-all duration-[var(--dur-base)]';
export const CHIP_ON =
  'inline-flex items-center gap-[7px] h-8 pl-[13px] pr-2 rounded-full bg-tick-affirm text-sage-800 text-[13px] font-semibold';

/**
 * ModalCloseButton — the prototype modal-head close affordance (icon-btn,
 * 34x34, no shadow). Passed through BottomSheet's `headerRight` slot so the
 * X sits to the right of the title.
 */
export function ModalCloseButton({ onClick }: { onClick: () => void }): ReactNode {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Close"
      className="w-[34px] h-[34px] rounded-full border border-sage-800/[8%] bg-paper text-sage-400 inline-flex items-center justify-center hover:text-sage-800 hover:border-sage-400/20 transition-colors duration-[var(--dur-base)]"
    >
      <X size={18} aria-hidden="true" />
    </button>
  );
}
